import {
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import {
  useTerminalDrawerActions,
  useTerminalDrawerState,
} from '#/features/terminal/drawer-context';
import type { TerminalTab } from '#/features/terminal/drawer-context';

import BlockTradeContextPump from '#/components/Terminal/BlockTradeContextPump';
import ChatView from '#/components/Terminal/ChatView';
import CronView from '#/components/Terminal/CronView';
import ExamplesView from '#/components/Terminal/ExamplesView';
import {
  SIDE_PANEL_CHAT_MAX_WIDTH,
  SIDE_PANEL_CHAT_MIN_WIDTH,
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
} from '#/components/Terminal/layout-constants';
import PendingMessageDrain from '#/components/Terminal/PendingMessageDrain';
import RecentsView from '#/components/Terminal/RecentsView';
import { SidebarResizeHandle } from '#/components/Terminal/SidebarResizeHandle';

export default function BlockTerminalTab() {
  return (
    <Container>
      <BlockTradeContextPump />
      <PendingMessageDrain />
      <Header />
      <Body />
    </Container>
  );
}

function Header() {
  const drawerState = useTerminalDrawerState();
  const drawerActions = useTerminalDrawerActions();
  return (
    <HeaderRow>
      <Title>DIME Terminal</Title>
      <Spacer />
      <HeaderControls>
        <SidebarToggleButton
          type="button"
          aria-label={
            drawerState.sidebarCollapsed ? 'Open sidebar' : 'Close sidebar'
          }
          title={
            drawerState.sidebarCollapsed ? 'Open sidebar' : 'Close sidebar'
          }
          onClick={drawerActions.toggleSidebar}
          $active={!drawerState.sidebarCollapsed}
        >
          <SidebarToggleIcon />
        </SidebarToggleButton>
        <PinSegment role="group" aria-label="Terminal pin mode">
          <PinSegmentButton
            $active={drawerState.pinMode === 'bottom-tab'}
            onClick={() => drawerActions.setPinMode('bottom-tab')}
            type="button"
            title="Pin to bottom"
            aria-label="Pin to bottom"
            aria-pressed={drawerState.pinMode === 'bottom-tab'}
          >
            <BottomDockIcon />
          </PinSegmentButton>
          <PinSegmentButton
            $active={drawerState.pinMode === 'side-panel'}
            onClick={() => {
              drawerActions.setPinMode('side-panel');
              // Ensure drawer stays open when switching to side-panel
              if (!drawerState.isOpen) {
                drawerActions.open();
              }
            }}
            type="button"
            title="Pin to side"
            aria-label="Pin to side"
            aria-pressed={drawerState.pinMode === 'side-panel'}
          >
            <SideDockIcon />
          </PinSegmentButton>
        </PinSegment>
      </HeaderControls>
    </HeaderRow>
  );
}

function Body() {
  const drawerState = useTerminalDrawerState();
  const drawerActions = useTerminalDrawerActions();

  // The "Recents" tab used to be one of the top-level tabs; the
  // sidebar replaces it. If a previous session persisted
  // `activeTab: 'recents'` in localStorage, redirect to chat so the
  // main pane always has something concrete to render.
  useEffect(() => {
    if (drawerState.activeTab === ('recents' as TerminalTab)) {
      drawerActions.setActiveTab('chat');
    }
  }, [drawerState.activeTab, drawerActions]);

  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startWidth: number;
    currentWidth: number;
    // Side-panel mode preserves the outer panel width by writing inverse
    // deltas to the chat pane — capture the starting chat width and the
    // mode snapshot at pointer-down so the move handler doesn't have to
    // read live drawer state mid-drag (and so a pinMode flip mid-drag
    // doesn't switch behavior partway through a gesture).
    startChatWidth: number;
    currentChatWidth: number;
    inSidePanel: boolean;
  } | null>(null);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startWidth: drawerState.sidebarWidth,
        currentWidth: drawerState.sidebarWidth,
        startChatWidth: drawerState.sidePanelChatWidth,
        currentChatWidth: drawerState.sidePanelChatWidth,
        inSidePanel: drawerState.pinMode === 'side-panel',
      };
      setIsDragging(true);
    },
    [
      drawerState.sidebarWidth,
      drawerState.sidePanelChatWidth,
      drawerState.pinMode,
    ],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const r = dragRef.current;
      if (r == null || r.pointerId !== e.pointerId) return;
      // Defensive: same fix as the outer-edge handler in
      // BlockTerminalSidePanel — if no button is held the drag has
      // effectively ended without a paired pointerup, so bail out and
      // clear state instead of resizing on bare hover.
      if (e.buttons === 0) {
        dragRef.current = null;
        setIsDragging(false);
        return;
      }
      const desiredSidebar = r.startWidth + (e.clientX - r.startX);
      if (!r.inSidePanel) {
        // Bottom-tab mode: the chat pane is flex:1 and absorbs whatever
        // the sidebar doesn't take, so the sidebar moves independently.
        const next = clamp(
          desiredSidebar,
          SIDEBAR_MIN_WIDTH,
          SIDEBAR_MAX_WIDTH,
        );
        r.currentWidth = next;
        drawerActions.setSidebarWidth(next);
        return;
      }
      // Side-panel mode: preserve the total panel width (sidebar + chat).
      // Resizing the panel's outer dimension lives on the outer left edge
      // (BlockTerminalSidePanel); the middle divider just redistributes
      // inside the existing total. Joint clamp keeps both ends within
      // their own min/max as the sum stays constant.
      const total = r.startWidth + r.startChatWidth;
      const minSidebar = Math.max(
        SIDEBAR_MIN_WIDTH,
        total - SIDE_PANEL_CHAT_MAX_WIDTH,
      );
      const maxSidebar = Math.min(
        SIDEBAR_MAX_WIDTH,
        total - SIDE_PANEL_CHAT_MIN_WIDTH,
      );
      const nextSidebar = clamp(desiredSidebar, minSidebar, maxSidebar);
      const nextChat = total - nextSidebar;
      r.currentWidth = nextSidebar;
      r.currentChatWidth = nextChat;
      drawerActions.setSidebarWidth(nextSidebar);
      drawerActions.setSidePanelChatWidth(nextChat);
    },
    [drawerActions],
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const r = dragRef.current;
      if (r == null || r.pointerId !== e.pointerId) return;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // already released — ignore
      }
      // Persist once at end of drag — pointer-move only wrote to
      // in-memory state to avoid 60-writes-per-second to localStorage.
      drawerActions.commitSidebarWidth(r.currentWidth);
      if (r.inSidePanel) {
        drawerActions.commitSidePanelChatWidth(r.currentChatWidth);
      }
      dragRef.current = null;
      setIsDragging(false);
    },
    [drawerActions],
  );

  const showResizeHandle = !drawerState.sidebarCollapsed;

  return (
    <BodyContainer>
      {!drawerState.sidebarCollapsed && (
        <Sidebar $width={drawerState.sidebarWidth}>
          <RecentsView />
        </Sidebar>
      )}
      {showResizeHandle && (
        <SidebarResizeHandle
          style={{ left: drawerState.sidebarWidth }}
          $dragging={isDragging}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
      )}
      <MainPane>
        <TabPane $active={drawerState.activeTab === 'chat'}>
          <ChatView hideNewChatBar />
        </TabPane>
        <TabPane $active={drawerState.activeTab === 'examples'}>
          <ExamplesView />
        </TabPane>
        <TabPane $active={drawerState.activeTab === 'cron'}>
          <CronView />
        </TabPane>
      </MainPane>
    </BodyContainer>
  );
}

function SidebarToggleIcon() {
  // Bordered rectangle with a vertical divider — same glyph as the
  // Paradex `SidebarToggleIcon` so the action reads the same in both
  // apps.
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <rect
        x="3"
        y="4"
        width="18"
        height="16"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <line
        x1="9"
        y1="4"
        x2="9"
        y2="20"
        stroke="currentColor"
        strokeWidth="1.6"
      />
    </svg>
  );
}

const Container = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 8px;
  border-bottom: 1px solid var(--border-color, rgba(255, 255, 255, 0.08));
`;

const Title = styled.h2`
  margin: 0;
  padding: 0 4px;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1;
  color: #fff;
  letter-spacing: 0.02em;
`;

const Spacer = styled.div`
  flex: 1;
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SidebarToggleButton = styled.button<{ $active: boolean }>`
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  color: ${(p) => (p.$active ? '#fff' : 'var(--text-secondary)')};
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    color: #fff;
  }
`;

const PinSegment = styled.div`
  display: flex;
  border: 1px solid var(--border-color, rgba(255, 255, 255, 0.12));
  border-radius: 6px;
  overflow: hidden;
`;

const PinSegmentButton = styled.button<{ $active: boolean }>`
  /* Icon-only — size the hit area generously around the 14px glyph so
     the segmented control still has Bottom/Side's old visual footprint. */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 22px;
  padding: 0;
  background: ${(p) =>
    p.$active ? 'var(--hover-bg, rgba(255, 255, 255, 0.08))' : 'transparent'};
  color: ${(p) =>
    p.$active ? 'var(--text-primary)' : 'var(--text-secondary)'};
  border: none;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  & + & {
    border-left: 1px solid var(--border-color, rgba(255, 255, 255, 0.12));
  }

  &:hover {
    color: var(--text-primary);
  }
`;

/**
 * Small mockup of the app window with the terminal docked at the bottom
 * (filled strip across the lower third). Inherits color so the active /
 * inactive states from PinSegmentButton flow through unchanged.
 */
function BottomDockIcon() {
  return (
    <DockIconSvg viewBox="0 0 14 14" aria-hidden="true">
      <rect
        x="1"
        y="1"
        width="12"
        height="12"
        rx="1.75"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeOpacity="0.7"
      />
      <rect
        x="2.25"
        y="8.25"
        width="9.5"
        height="3.5"
        rx="0.6"
        fill="currentColor"
      />
    </DockIconSvg>
  );
}

/**
 * Same window mockup with the terminal docked on the right edge.
 */
function SideDockIcon() {
  return (
    <DockIconSvg viewBox="0 0 14 14" aria-hidden="true">
      <rect
        x="1"
        y="1"
        width="12"
        height="12"
        rx="1.75"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeOpacity="0.7"
      />
      <rect
        x="8.25"
        y="2.25"
        width="3.5"
        height="9.5"
        rx="0.6"
        fill="currentColor"
      />
    </DockIconSvg>
  );
}

const DockIconSvg = styled.svg`
  width: 14px;
  height: 14px;
  display: block;
  flex-shrink: 0;
`;

const BodyContainer = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: row;
  position: relative;
`;

const Sidebar = styled.div<{ readonly $width: number }>`
  flex-shrink: 0;
  width: ${({ $width }) => $width}px;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--border-color, rgba(255, 255, 255, 0.08));
`;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

const MainPane = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const TabPane = styled.div<{ $active: boolean }>`
  display: ${(p) => (p.$active ? 'flex' : 'none')};
  flex: 1;
  min-height: 0;
  flex-direction: column;
`;

// Re-export drawer types so other components can flag-gate without an
// extra import.
export type { PinMode, TerminalTab } from '#/features/terminal/drawer-context';
